/** 釋疑圖形：雲狀圖、箭頭、手繪、文字、方框。座標在建立當下的視平面上，存成模型座標。 */

export const MARKUP_TYPES = Object.freeze({
  cloud: '雲狀圖', arrow: '箭頭', ink: '手繪', text: '文字', rect: '方框',
});

const LIMITS = { cloud: [3, 40], arrow: [2, 2], ink: [2, 160], text: [1, 1], rect: [2, 2] };
const round = n => Math.round(n * 1e6) / 1e6;

const fail = message => { throw Object.assign(new Error(message), { status: 400 }); };

export function normalizeMarkup(raw) {
  if (raw == null) return null;
  if (!raw || typeof raw !== 'object' || !LIMITS[raw.type]) fail('釋疑圖形類型無效');
  const [min, max] = LIMITS[raw.type];
  if (!Array.isArray(raw.points) || raw.points.length < min || raw.points.length > max) fail('釋疑圖形點數無效');
  const points = raw.points.map(p => {
    if (!Array.isArray(p) || p.length !== 2 || !p.every(n => Number.isFinite(n) && Math.abs(n) <= 1e9)) fail('釋疑圖形座標無效');
    return [round(p[0]), round(p[1])];
  });
  const axis = (value, name) => {
    if (!Array.isArray(value) || value.length !== 3 || !value.every(n => Number.isFinite(n))) fail(`${name}無效`);
    const len = Math.hypot(...value);
    if (len < 0.5 || len > 1.5) fail(`${name}無效`);
    return value.map(n => round(n / len));
  };
  if (!Array.isArray(raw.origin) || raw.origin.length !== 3 || !raw.origin.every(n => Number.isFinite(n) && Math.abs(n) <= 1e12)) fail('釋疑圖形原點無效');
  const axisX = axis(raw.axisX, '釋疑圖形方向');
  const axisY = axis(raw.axisY, '釋疑圖形方向');
  const dot = axisX[0] * axisY[0] + axisX[1] * axisY[1] + axisX[2] * axisY[2];
  if (Math.abs(dot) > 0.2) fail('釋疑圖形方向無效');
  return { type: raw.type, origin: raw.origin.map(round), axisX, axisY, points };
}

export function markupAnchor(markup) {
  const [u, v] = markup.points[0];
  return markup.origin.map((n, i) => round(n + markup.axisX[i] * u + markup.axisY[i] * v));
}

function world(markup, uv) {
  const [u, v] = uv;
  return markup.origin.map((n, i) => n + markup.axisX[i] * u + markup.axisY[i] * v);
}

function arc(a, b, steps) {
  const dx = b[0] - a[0], dy = b[1] - a[1], len = Math.hypot(dx, dy) || 1;
  const r = Math.max(len * 0.62, 1e-9);
  const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
  const h = Math.sqrt(Math.max(r * r - (len / 2) ** 2, 0));
  const cx = mx + (-dy / len) * h, cy = my + (dx / len) * h;
  const a0 = Math.atan2(a[1] - cy, a[0] - cx);
  let delta = Math.atan2(b[1] - cy, b[0] - cx) - a0;
  while (delta <= -Math.PI) delta += Math.PI * 2;
  while (delta > Math.PI) delta -= Math.PI * 2;
  const out = [];
  for (let i = 0; i <= steps; i++) {
    const t = a0 + delta * (i / steps);
    out.push([cx + r * Math.cos(t), cy + r * Math.sin(t)]);
  }
  return out;
}

function cloudSamples(pts) {
  const out = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    const first = arc(a, mid, 4), second = arc(mid, b, 4);
    out.push(...(i ? first.slice(1) : first), ...second.slice(1));
  }
  return out;
}

/** 轉成模型座標折線。fill 為箭頭三角。 */
export function markupPolylines(markup) {
  if (!markup) return [];
  const pts = markup.points;
  if (markup.type === 'cloud') return [{ points: cloudSamples(pts).map(p => world(markup, p)) }];
  if (markup.type === 'ink') return [{ points: pts.map(p => world(markup, p)) }];
  if (markup.type === 'rect') {
    const [a, b] = pts;
    return [{ points: [a, [b[0], a[1]], b, [a[0], b[1]], a].map(p => world(markup, p)) }];
  }
  if (markup.type === 'arrow') {
    const [a, b] = pts;
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
    const head = Math.max(len * 0.18, 1e-6);
    const ang = Math.atan2(b[1] - a[1], b[0] - a[0]), spread = 0.45;
    const tip = b;
    const left = [b[0] - head * Math.cos(ang - spread), b[1] - head * Math.sin(ang - spread)];
    const right = [b[0] - head * Math.cos(ang + spread), b[1] - head * Math.sin(ang + spread)];
    return [{ points: [world(markup, a), world(markup, b)] }, { points: [tip, left, right].map(p => world(markup, p)), fill: true }];
  }
  return [];
}

export function decimate(points, max) {
  if (points.length <= max) return points;
  const out = [];
  const step = (points.length - 1) / (max - 1);
  for (let i = 0; i < max; i++) out.push(points[Math.round(i * step)]);
  return out;
}
